// Master Admin — Square Operations Console
// Single page exposing every Square admin endpoint + cleanup tooling +
// approved-roster seeding. Master-admin only.
import React, { useEffect, useState } from "react";
import {
  AlertTriangle, CheckCircle2, Loader2, RefreshCw, ShieldCheck,
  Box, Package, Image as ImageIcon, DollarSign, Database, Stethoscope,
  Trash2, Users, UserPlus, BarChart3, Receipt,
} from "lucide-react";
import api from "@/lib/api";

function fmtMoney(c) {
  if (c == null) return "$0.00";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })
    .format((Number(c) || 0) / 100);
}

function Card({ title, icon: Icon, children, testid }) {
  return (
    <section className="glass-panel p-5 space-y-3" data-testid={testid}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-lifted-green" />}
        <h2 className="font-anton uppercase text-lg text-white">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function ActionButton({ label, onClick, busy, danger = false, testid, icon: Icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-xs uppercase tracking-[0.16em] font-bebas transition-colors
        ${danger
          ? "bg-rose-500/10 hover:bg-rose-500/20 text-rose-200 border-rose-500/40"
          : "bg-lifted-green/10 hover:bg-lifted-green/20 text-lifted-green border-lifted-green/40"}
        disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> :
        (Icon ? <Icon size={14} /> : null)}
      {label}
    </button>
  );
}

function ResultBlock({ result, error }) {
  if (!result && !error) return null;
  return (
    <pre
      className={`mt-3 p-3 rounded-md text-[11px] font-mono overflow-x-auto max-h-72 border
        ${error
          ? "bg-rose-500/10 text-rose-200 border-rose-500/30"
          : "bg-black/30 text-lifted-dim border-white/10"}`}
      data-testid={error ? "action-error" : "action-result"}
    >
      {error ? `Error: ${error}` : JSON.stringify(result, null, 2)}
    </pre>
  );
}

export default function AdminSquareOperations() {
  const [revenue, setRevenue] = useState(null);
  const [revenueErr, setRevenueErr] = useState("");
  const [busy, setBusy] = useState({});
  const [results, setResults] = useState({});
  const [errors, setErrors] = useState({});

  async function loadRevenue() {
    try {
      const { data } = await api.get("/admin/dashboard/revenue");
      setRevenue(data);
      setRevenueErr("");
    } catch (e) {
      setRevenueErr(e?.response?.data?.detail || e?.message || "Failed to load");
    }
  }
  useEffect(() => { loadRevenue(); }, []);

  async function runAction(key, method, path, body, opts = {}) {
    if (opts.confirm && !window.confirm(opts.confirm)) return;
    setBusy((b) => ({ ...b, [key]: true }));
    setErrors((e) => ({ ...e, [key]: "" }));
    try {
      const fn = method === "post" ? api.post : api.get;
      const { data } = body ? await fn(path, body) : await fn(path);
      setResults((r) => ({ ...r, [key]: data }));
      if (opts.refreshRevenue) loadRevenue();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : (detail?.error_code || detail?.message || e?.message || "Action failed");
      setErrors((er) => ({ ...er, [key]: msg }));
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  }

  const Section = (k) => ({ result: results[k], error: errors[k], busy: busy[k] });

  return (
    <div
      className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6"
      data-testid="admin-square-operations"
    >
      <header className="flex items-baseline justify-between gap-3">
        <div>
          <div className="eyebrow text-lifted-dim">Master Admin</div>
          <h1 className="font-anton text-4xl uppercase text-white">Square Operations</h1>
          <p className="text-lifted-dim text-sm mt-1">
            Live revenue + Square catalog sync + cleanup tooling. All actions
            are audited and reversible (archive-only).
          </p>
        </div>
        <button
          type="button"
          onClick={loadRevenue}
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] font-bebas text-lifted-dim hover:text-lifted-green"
          data-testid="refresh-revenue"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </header>

      {/* Revenue dashboard */}
      <Card title="Live Revenue" icon={BarChart3} testid="revenue-card">
        {revenueErr && (
          <div className="text-rose-200 text-sm">{revenueErr}</div>
        )}
        {revenue && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Metric label="Total Revenue" value={fmtMoney(revenue.total_revenue_cents)} testid="m-revenue" />
            <Metric label="Square Verified" value={fmtMoney(revenue.square_verified_revenue_cents)} testid="m-square-rev" />
            <Metric label="Paid Orders" value={revenue.total_paid_orders} testid="m-paid-orders" />
            <Metric label="Pending" value={revenue.pending_orders} testid="m-pending" />
            <Metric label="Fulfilled" value={revenue.fulfilled_orders} testid="m-fulfilled" />
            <Metric label="Refunded" value={revenue.refunded_orders} testid="m-refunded" />
            <Metric label="Aff. Commission Pending" value={fmtMoney(revenue.affiliate_commission_pending_cents)} testid="m-aff-pending" />
            <Metric label="Partner Payout Pending" value={fmtMoney(revenue.partner_payout_pending_cents)} testid="m-partner-pending" />
          </div>
        )}
        {!revenue && !revenueErr && (
          <div className="text-lifted-dim text-sm flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        )}
      </Card>

      {/* Square sync controls */}
      <Card title="Square Catalog & Inventory" icon={Database} testid="square-sync-card">
        <div className="flex flex-wrap gap-2">
          <ActionButton label="Test Connection" testid="btn-square-test-connection"
            icon={Stethoscope} busy={busy.testConn}
            onClick={() => runAction("testConn", "get", "/admin/square/connection-test")} />
          <ActionButton label="Push All Products" testid="btn-square-sync-all"
            icon={Box} busy={busy.syncAll}
            onClick={() => runAction("syncAll", "post", "/admin/square/products/sync-all", {})} />
          <ActionButton label="Sync Product Images" testid="btn-square-image-sync"
            icon={ImageIcon} busy={busy.imgSync}
            onClick={() => runAction("imgSync", "post", "/admin/square/products/images/sync-all", {})} />
          <ActionButton label="Sync Inventory" testid="btn-square-inventory-sync"
            icon={Package} busy={busy.invSync}
            onClick={() => runAction("invSync", "post", "/admin/square/inventory/sync-all", {})} />
          <ActionButton label="Repair Prices" testid="btn-square-repair-prices"
            icon={DollarSign} busy={busy.repair}
            onClick={() => runAction("repair", "post", "/admin/square/products/repair-prices?apply=true", {})} />
          <ActionButton label="Reconcile Catalogs" testid="btn-square-reconcile"
            icon={RefreshCw} busy={busy.reconcile}
            onClick={() => runAction("reconcile", "post", "/admin/square/products/reconcile", {})} />
          <ActionButton label="Payment Audit" testid="btn-square-payment-audit"
            icon={Receipt} busy={busy.audit}
            onClick={() => runAction("audit", "get", "/admin/square/payment-audit")} />
          <ActionButton label="View Sync Status" testid="btn-square-sync-status"
            icon={ShieldCheck} busy={busy.syncStatus}
            onClick={() => runAction("syncStatus", "get", "/admin/square/sync-status")} />
          {/* Iter84 — diagnostics + full backfill + catalog purge */}
          <ActionButton label="Sync Diagnostics" testid="btn-square-diagnostics"
            icon={Stethoscope} busy={busy.diag}
            onClick={() => runAction("diag", "get", "/admin/square/sync-diagnostics")} />
          <ActionButton label="Backfill All to Square" testid="btn-square-backfill-all"
            icon={Box} busy={busy.backfillAll}
            onClick={() => runAction("backfillAll", "post", "/admin/square/backfill-products?include_archived=true", {})} />
          <ActionButton label="Catalog Audit" testid="btn-square-catalog-audit"
            icon={Database} busy={busy.catalogAudit}
            onClick={() => runAction("catalogAudit", "get", "/admin/square/catalog/audit")} />
          <ActionButton label="Purge Test Items (dry run)" testid="btn-square-purge-dry"
            icon={Trash2} busy={busy.purgeDry}
            onClick={() => runAction("purgeDry", "post", "/admin/square/catalog/purge-test-items?dry_run=true", {})} />
          <ActionButton label="Purge Test Items (commit)" testid="btn-square-purge-commit"
            danger icon={Trash2} busy={busy.purgeCommit}
            onClick={() => runAction("purgeCommit", "post", "/admin/square/catalog/purge-test-items?dry_run=false", {},
              { confirm: "Permanently delete Iter66–Iter82 test items from the live Square catalogue? Real customer items will NOT be touched." })} />
        </div>
        {/* Results, one block per action */}
        {Object.keys({...busy, ...results, ...errors}).filter(k => k !== "cleanupOrders" && k !== "cleanupUsers" && k !== "seedRoles").map((k) => (
          (results[k] || errors[k]) ? (
            <details key={k} className="text-xs mt-1" data-testid={`result-${k}`}>
              <summary className="text-lifted-dim font-mono cursor-pointer">{k}</summary>
              <ResultBlock result={results[k]} error={errors[k]} />
            </details>
          ) : null
        ))}
      </Card>

      {/* Cleanup tooling */}
      <Card title="Data Cleanup (Archive Only)" icon={Trash2} testid="cleanup-card">
        <p className="text-lifted-dim text-xs">
          All cleanups are <strong className="text-white">soft archive</strong>: rows stay in MongoDB
          with <code>account_status=archived_cleanup</code> or <code>archived_at</code>.
          Run dry-run first to preview counts.
        </p>
        <div className="grid sm:grid-cols-2 gap-4 mt-2">
          <div className="space-y-2">
            <div className="font-bebas uppercase text-xs text-white tracking-[0.18em]">Orders</div>
            <div className="flex flex-wrap gap-2">
              <ActionButton label="Export CSV" testid="btn-export-orders"
                onClick={() => {
                  window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/export-before-cleanup`, "_blank");
                }} />
              <ActionButton label="Dry Run" testid="btn-orders-cleanup-dry"
                busy={busy.orderDry}
                onClick={() => runAction("orderDry", "post", "/admin/orders/cleanup-test-orders", { dry_run: true })} />
              <ActionButton label="Archive Test Orders" testid="btn-orders-cleanup-exec"
                danger busy={busy.cleanupOrders}
                onClick={() => runAction("cleanupOrders", "post", "/admin/orders/cleanup-test-orders", {},
                  { confirm: "Archive all orders except Jesse Workman's? This is reversible.", refreshRevenue: true })} />
            </div>
            <ResultBlock result={results.orderDry || results.cleanupOrders}
                          error={errors.orderDry || errors.cleanupOrders} />
          </div>
          <div className="space-y-2">
            <div className="font-bebas uppercase text-xs text-white tracking-[0.18em]">Customers</div>
            <div className="flex flex-wrap gap-2">
              <ActionButton label="Export CSV" testid="btn-export-users"
                onClick={() => {
                  window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/exports/users.csv`, "_blank");
                }} />
              <ActionButton label="Dry Run" testid="btn-users-cleanup-dry"
                busy={busy.userDry}
                onClick={() => runAction("userDry", "post", "/admin/users/cleanup-active-users", { dry_run: true })} />
              <ActionButton label="Archive Non-Approved" testid="btn-users-cleanup-exec"
                danger busy={busy.cleanupUsers}
                onClick={() => runAction("cleanupUsers", "post", "/admin/users/cleanup-active-users", {},
                  { confirm: "Archive everyone except the 8 approved customers + admins? Reversible." })} />
              <ActionButton label="Seed Approved Roles" testid="btn-seed-roles"
                icon={UserPlus} busy={busy.seedRoles}
                onClick={() => runAction("seedRoles", "post", "/admin/users/seed-approved-roles", {})} />
            </div>
            <ResultBlock result={results.userDry || results.cleanupUsers || results.seedRoles}
                          error={errors.userDry || errors.cleanupUsers || errors.seedRoles} />
          </div>
        </div>
      </Card>

      <p className="text-[11px] text-lifted-dim font-mono">
        All operations write to <code>audit_log</code> with action keys
        <code> square.* · orders.cleanup · users.cleanup · users.seed_approved_roles</code>.
      </p>
    </div>
  );
}

function Metric({ label, value, testid }) {
  return (
    <div className="glass-panel p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bebas text-lifted-dim">{label}</div>
      <div className="text-white font-anton text-2xl mt-1">{value}</div>
    </div>
  );
}
